import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Save, Trash2, Loader2, PlugZap, CheckCircle2, XCircle, Eye, EyeOff, BarChart3 } from "lucide-react";
import { formatRupiah } from "../lib/format";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const PROVIDER_LABEL = {
    openai_personal_db: { l: "OpenAI Pribadi (DB)", color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
    openai_personal_env: { l: "OpenAI Pribadi (ENV)", color: "text-teal-700 bg-teal-50 border-teal-200" },
    emergent_universal: { l: "Emergent Universal", color: "text-indigo-700 bg-indigo-50 border-indigo-200" },
    none: { l: "Belum Diatur", color: "text-red-700 bg-red-50 border-red-200" },
};

export default function KonfigurasiAI() {
    const [cfg, setCfg] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [form, setForm] = useState({ openai_api_key: "", model: "gpt-4o-mini", rate_limit: 20 });
    const [showKey, setShowKey] = useState(false);
    const [logs, setLogs] = useState(null);
    const [tab, setTab] = useState("config");

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/ai/config");
            setCfg(r.data || {});
            setForm((f) => ({
                ...f,
                model: r.data?.model || "gpt-4o-mini",
                rate_limit: r.data?.rate_limit ?? 20,
                openai_api_key: "",
            }));
        } catch (e) {
            setCfg({});
            toast.error(e.response?.data?.detail || "Gagal memuat config");
        } finally { setLoading(false); }
    };
    const loadLogs = async () => {
        try {
            const r = await api.get("/ai/logs?limit=50");
            setLogs(r.data);
        } catch (e) { toast.error("Gagal memuat log"); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
    useEffect(() => { if (tab === "logs") loadLogs(); /* eslint-disable-next-line */ }, [tab]);

    const save = async () => {
        const payload = {};
        if (form.openai_api_key?.trim()) payload.openai_api_key = form.openai_api_key.trim();
        if (form.model) payload.model = form.model;
        if (form.rate_limit !== cfg?.rate_limit) payload.rate_limit = Number(form.rate_limit);
        if (Object.keys(payload).length === 0) return toast.info("Tidak ada perubahan");
        setSaving(true);
        try {
            await api.post("/ai/config", payload);
            toast.success("Konfigurasi AI disimpan");
            setForm((f) => ({ ...f, openai_api_key: "" }));
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Gagal simpan"); }
        finally { setSaving(false); }
    };

    const hapusKey = async () => {
        if (!window.confirm("Hapus API key OpenAI pribadi dari database? Sistem akan fallback ke env/Emergent.")) return;
        try {
            await api.delete("/ai/config/key");
            toast.success("API key pribadi dihapus");
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Gagal hapus"); }
    };

    const testKoneksi = async () => {
        setTesting(true); setTestResult(null);
        try {
            const r = await api.post("/ai/test");
            setTestResult(r.data);
            if (r.data.ok) toast.success("Koneksi AI sukses");
            else toast.error(r.data.message || "Test gagal");
        } catch (e) {
            setTestResult({ ok: false, message: e.response?.data?.detail || "Error" });
        } finally { setTesting(false); }
    };

    if (loading) {
        return <div className="p-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Memuat konfigurasi...</div>;
    }

    const provBadge = PROVIDER_LABEL[cfg?.provider] || PROVIDER_LABEL.none;

    return (
        <div className="p-4 md:p-6 max-w-4xl mx-auto">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                    <Sparkles className="w-6 h-6 text-indigo-600" />
                    Konfigurasi AI
                </h1>
                <p className="text-sm text-slate-500 mt-1">
                    Atur API key OpenAI pribadi, model AI, dan batas penggunaan harian.
                </p>
            </div>

            <div className="flex gap-1 border-b border-slate-200 mb-4">
                {[["config", "Konfigurasi"], ["logs", "Penggunaan AI"]].map(([k, l]) => (
                    <button
                        key={k}
                        onClick={() => setTab(k)}
                        data-testid={`konfig-ai-tab-${k}`}
                        className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                            tab === k ? "border-indigo-600 text-indigo-700" : "border-transparent text-slate-500 hover:text-slate-700"
                        }`}
                    >
                        {l}
                    </button>
                ))}
            </div>

            {tab === "config" && (
                <>
                    {/* Status sumber key */}
                    <div className={`panel border p-4 mb-4 ${provBadge.color}`} data-testid="ai-provider-status">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                            <div>
                                <div className="text-[10px] font-bold uppercase tracking-wider opacity-70 mb-1">Sumber API Aktif</div>
                                <div className="text-base font-bold" data-testid="ai-provider-label">{provBadge.l}</div>
                                {cfg?.key_masked && (
                                    <div className="text-xs font-mono mt-1 opacity-80" data-testid="ai-key-masked">
                                        Key: {cfg.key_masked}
                                    </div>
                                )}
                            </div>
                            <div className="text-xs">
                                <div>Model: <b className="font-mono">{cfg?.model || "—"}</b></div>
                                <div>Batas Harian: <b>{cfg?.rate_limit === 0 ? "unlimited" : (cfg?.rate_limit ?? "—")}</b></div>
                            </div>
                        </div>
                    </div>

                    {/* Form config */}
                    <div className="panel p-5 bg-white mb-4">
                        <h2 className="font-semibold text-slate-900 mb-4">Setting</h2>

                        <div className="mb-4">
                            <label className="block text-xs font-medium text-slate-700 mb-1">
                                OpenAI API Key Pribadi <span className="text-slate-400 font-normal">(opsional — simpan di DB)</span>
                            </label>
                            <div className="relative">
                                <input
                                    type={showKey ? "text" : "password"}
                                    value={form.openai_api_key}
                                    onChange={(e) => setForm({ ...form, openai_api_key: e.target.value })}
                                    placeholder="sk-proj-... (biarkan kosong kalau tidak mau ubah)"
                                    data-testid="konfig-ai-key-input"
                                    className="w-full border border-slate-300 rounded px-3 py-2 pr-10 text-sm font-mono"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowKey((v) => !v)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                    data-testid="konfig-ai-key-toggle"
                                >
                                    {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                            <div className="text-[11px] text-slate-500 mt-1">
                                Prioritas sumber: Key di DB → env OPENAI_API_KEY → Emergent Universal Key (fallback).
                            </div>
                            {cfg?.has_db_key && (
                                <button
                                    onClick={hapusKey}
                                    data-testid="konfig-ai-key-delete"
                                    className="mt-2 text-xs text-red-600 hover:underline inline-flex items-center gap-1"
                                >
                                    <Trash2 className="w-3 h-3" /> Hapus key pribadi dari DB
                                </button>
                            )}
                        </div>

                        <div className="mb-4">
                            <label className="block text-xs font-medium text-slate-700 mb-1">Model AI</label>
                            <select
                                value={form.model}
                                onChange={(e) => setForm({ ...form, model: e.target.value })}
                                data-testid="konfig-ai-model"
                                className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
                            >
                                {(cfg?.allowed_models || ["gpt-4o-mini", "gpt-4o"]).map((m) => <option key={m} value={m}>{m}</option>)}
                            </select>
                            <div className="text-[11px] text-slate-500 mt-1">
                                gpt-4o-mini: termurah &amp; cepat · gpt-4o: lebih pintar &amp; mahal · gpt-4.1: terbaru
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="block text-xs font-medium text-slate-700 mb-1">
                                Batas Request/Hari (prediksi + ringkasan)
                            </label>
                            <input
                                type="number"
                                min={0}
                                max={10000}
                                value={form.rate_limit}
                                onChange={(e) => setForm({ ...form, rate_limit: e.target.value })}
                                data-testid="konfig-ai-rate-limit"
                                className="w-32 border border-slate-300 rounded px-3 py-2 text-sm"
                            />
                            <span className="ml-2 text-xs text-slate-500">0 = unlimited</span>
                        </div>

                        <div className="flex gap-2 justify-end pt-3 border-t border-slate-100">
                            <button
                                onClick={testKoneksi}
                                disabled={testing}
                                data-testid="konfig-ai-test"
                                className="px-4 py-2 border border-indigo-600 text-indigo-700 hover:bg-indigo-50 rounded text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50"
                            >
                                {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlugZap className="w-3.5 h-3.5" />}
                                Test Koneksi
                            </button>
                            <button
                                onClick={save}
                                disabled={saving}
                                data-testid="konfig-ai-save"
                                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50"
                            >
                                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                                Simpan
                            </button>
                        </div>
                    </div>

                    {/* Hasil test */}
                    {testResult && (
                        <div
                            className={`panel p-3 mb-4 ${testResult.ok ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`}
                            data-testid="konfig-ai-test-result"
                        >
                            <div className={`flex items-start gap-2 text-sm ${testResult.ok ? "text-emerald-800" : "text-red-800"}`}>
                                {testResult.ok ? <CheckCircle2 className="w-4 h-4 mt-0.5" /> : <XCircle className="w-4 h-4 mt-0.5" />}
                                <div className="flex-1">
                                    <div className="font-semibold">{testResult.message}</div>
                                    {testResult.response && (
                                        <div className="text-xs mt-1 font-mono opacity-80">Respon: "{testResult.response}"</div>
                                    )}
                                    {testResult.tokens_in != null && (
                                        <div className="text-[11px] mt-1 opacity-70">
                                            Tokens: {testResult.tokens_in} in / {testResult.tokens_out} out
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}

            {tab === "logs" && (
                <>
                    {!logs && <div className="panel p-6 text-center text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Memuat log...</div>}
                    {logs && (
                        <>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                                <StatCard label="Total Biaya 30 Hari" nilai={formatRupiah(logs?.cost_30d_rp || 0)} icon={<BarChart3 className="w-4 h-4" />} />
                                <StatCard label="Token Input 30 Hari" nilai={(logs?.tokens_in_30d || 0).toLocaleString("id-ID")} />
                                <StatCard label="Token Output 30 Hari" nilai={(logs?.tokens_out_30d || 0).toLocaleString("id-ID")} />
                            </div>

                            <BiayaHarianChart data={logs?.per_hari_30d || []} />

                            <div className="panel overflow-x-auto">
                                <div className="p-3 border-b border-slate-200">
                                    <h3 className="font-semibold text-sm text-slate-900">Log Pemakaian Terakhir ({logs?.logs?.length || 0})</h3>
                                </div>
                                <table className="table-nota" data-testid="konfig-ai-logs-table">
                                    <thead>
                                        <tr>
                                            <th>Tanggal</th>
                                            <th>Endpoint</th>
                                            <th>User</th>
                                            <th className="text-right">In</th>
                                            <th className="text-right">Out</th>
                                            <th className="text-right">Biaya</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(logs?.logs?.length || 0) === 0 && (
                                            <tr><td colSpan={6} className="text-center py-6 text-slate-400 italic text-xs">Belum ada pemakaian AI</td></tr>
                                        )}
                                        {(logs?.logs || []).map((l) => (
                                            <tr key={l.id}>
                                                <td className="font-mono text-xs">{l.created_at?.slice(0, 16).replace("T", " ")}</td>
                                                <td><span className="badge badge-biru">{l.endpoint}</span></td>
                                                <td>{l.username || "-"}</td>
                                                <td className="num">{l.prompt_tokens}</td>
                                                <td className="num">{l.completion_tokens}</td>
                                                <td className="num num-debit">{formatRupiah(l.cost_estimate_rp)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
}

function StatCard({ label, nilai, icon }) {
    return (
        <div className="panel p-3">
            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                {icon} {label}
            </div>
            <div className="text-lg font-bold text-slate-900 num-tabular">{nilai}</div>
        </div>
    );
}

function fmtDMY(iso) {
    if (!iso) return "-";
    const p = iso.split("-");
    return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso;
}

function fmtRpShort(v) {
    const n = Number(v) || 0;
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} jt`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(0)} rb`;
    return String(Math.round(n));
}

function BarTooltip({ active, payload }) {
    if (!active || !payload || !payload.length) return null;
    const p = payload[0].payload;
    return (
        <div className="bg-slate-900 text-white text-xs rounded px-2.5 py-1.5 shadow-lg" data-testid="chart-tooltip">
            <div className="font-mono">{fmtDMY(p.tanggal)}</div>
            <div className="font-bold">{formatRupiah(p.cost_rp)}</div>
        </div>
    );
}

function BiayaHarianChart({ data }) {
    // Fill missing days with 0 untuk 30 hari ke belakang
    const filled = (() => {
        const map = new Map(data.map((d) => [d.tanggal, d.cost_rp]));
        const out = [];
        const today = new Date();
        for (let i = 29; i >= 0; i--) {
            const d = new Date(today);
            d.setDate(d.getDate() - i);
            const iso = d.toISOString().slice(0, 10);
            out.push({ tanggal: iso, cost_rp: Number(map.get(iso) || 0), label: iso.slice(8) });
        }
        return out;
    })();

    const hasAny = filled.some((d) => d.cost_rp > 0);

    return (
        <div className="panel p-4 mb-4" data-testid="chart-biaya-harian">
            <div className="flex items-center gap-2 mb-3">
                <BarChart3 className="w-4 h-4 text-teal-600" />
                <h3 className="font-semibold text-sm text-slate-900">Biaya Harian (30 Hari Terakhir)</h3>
            </div>
            {!hasAny && (
                <div className="text-center py-8 text-xs text-slate-400 italic">
                    Belum ada pemakaian AI dalam 30 hari terakhir.
                </div>
            )}
            {hasAny && (
                <div className="w-full" style={{ height: 180 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={filled} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                            <XAxis
                                dataKey="label"
                                tick={{ fontSize: 10, fill: "#64748b" }}
                                tickLine={false}
                                interval={1}
                            />
                            <YAxis
                                tick={{ fontSize: 10, fill: "#64748b" }}
                                tickFormatter={fmtRpShort}
                                tickLine={false}
                                axisLine={false}
                                width={48}
                            />
                            <Tooltip content={<BarTooltip />} cursor={{ fill: "rgba(20,184,166,0.08)" }} />
                            <Bar dataKey="cost_rp" fill="#14B8A6" radius={[3, 3, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}
            <div className="text-[10px] text-slate-400 mt-2 text-right">
                Hover batang untuk lihat detail · Format: tanggal · Rp
            </div>
        </div>
    );
}
